/**
 * One-shot migration: rewrite workspace tree leaves `component: "chart"` →
 * `"advancedChart"` so saved Dashboard / Charting / Top-Down layouts pick up
 * the Advanced Chart embed after the Price→Advanced cutover.
 *
 * Walks:
 *   - the shared library pool (`mpp:workspaces:v1`)
 *   - every `mpp:ws-tree:{id}:v1` override (and Display Profile physical slots
 *     that share that prefix — localStorage enumeration sees the remapped keys
 *     when a profile is active, and the baseline keys otherwise)
 *
 * Idempotent. Flagged by `mpp:migrate:chart-to-advanced:v1`.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import type { WsNode, WorkspacePage } from "@/lib/workspace/workspacePages";

const FLAG = "mpp:migrate:chart-to-advanced:v1";
const LIST_KEY = "mpp:workspaces:v1";
const FROM = "chart";
const TO = "advancedChart";

function rewriteNode(n: WsNode): { node: WsNode; changed: boolean } {
  if (n.t === "leaf") {
    if (n.component === FROM) return { node: { ...n, component: TO }, changed: true };
    return { node: n, changed: false };
  }
  let changed = false;
  const children = n.children.map((c) => {
    const r = rewriteNode(c);
    if (r.changed) changed = true;
    return r.node;
  });
  return changed ? { node: { ...n, children }, changed: true } : { node: n, changed: false };
}

function rewriteTreeJson(raw: string): string | null {
  try {
    const root = JSON.parse(raw) as WsNode;
    if (!root || (root.t !== "leaf" && root.t !== "split")) return null;
    const { node, changed } = rewriteNode(root);
    return changed ? JSON.stringify(node) : null;
  } catch {
    return null;
  }
}

/** Run once per browser profile. Safe to call on every app boot.
 *  Does NOT call loadPages/savePages — those call back into this migrator. */
export function migrateChartLeavesToAdvanced(): void {
  if (typeof window === "undefined") return;
  if (safeLocalStorageGet(FLAG) === "1") return;

  // 1. Shared library pool (read/write the key directly to avoid recursion
  // through loadPages → migrateChartLeavesToAdvanced).
  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }
  let poolChanged = false;
  const nextPool: WorkspacePage[] = pool.map((p) => {
    if (!p?.root) return p;
    const { node, changed } = rewriteNode(p.root);
    if (!changed) return p;
    poolChanged = true;
    return { ...p, root: node, updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  // 2. Per-screen / per-profile tree overrides — enumerate every key that
  // looks like a ws-tree blob (including `\0@dp:` physical slots).
  try {
    const doomed: Array<{ key: string; value: string }> = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.includes("mpp:ws-tree:")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const next = rewriteTreeJson(raw);
      if (next) doomed.push({ key, value: next });
    }
    for (const { key, value } of doomed) {
      // Write through the patched stack so profile remapping still applies.
      safeLocalStorageSet(key.includes("\u0000@dp:") ? key.split("\u0000@dp:")[0]! : key, value);
      // Physical slots: write the physical key directly if safeLocalStorage
      // remapped away from it (baseline write-through). Fall back to raw set.
      if (key.includes("\u0000@dp:")) {
        try {
          window.localStorage.setItem(key, value);
        } catch {
          /* quota — the logical write above is the important one */
        }
      }
    }
  } catch {
    /* enumeration failed — pool migration still stands */
  }

  safeLocalStorageSet(FLAG, "1");
}
